import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { dateFormatter } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { format, isValid as isValidDate } from 'date-fns';
import { useState } from 'react';
import styled from 'styled-components';
import { EXECUTE_ACTION_MUTATION, REMAINING_RECIPIENTS_LIST_QUERY } from '../queries';
import { AllocationWithUpdated } from '../table/table';

interface ResendSignaturePacketProps {
  isOpen: boolean;
  currentRow: AllocationWithUpdated;
  onClose: () => void;
}

const ResendSignaturePacket = ({ isOpen, currentRow, onClose }: ResendSignaturePacketProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [recipientsList, setRecipientsList] = useState('');
  const [lastSentAt, setLastSentAt] = useState('');

  const [executeAction, { loading: resendESignatureRequestLoading }] = useMutation(EXECUTE_ACTION_MUTATION);

  const { loading: remainingRecipientsListLoading } = useQuery(REMAINING_RECIPIENTS_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      subscriptionDocumentId: currentRow?.subscriptionDocument?.id
    },
    onCompleted: (data) => {
      const { recipients, lastSentAt } = data?.remainingRecipientsList || {};
      setRecipientsList(recipients?.map((recipient: any) => recipient.name)?.join(', '));
      if (lastSentAt && isValidDate(new Date(lastSentAt))) {
        setLastSentAt(dateFormatter(format(new Date(lastSentAt), 'yyyy-MM-dd'), 'yyyy-MM-dd'));
      }
    }
  });

  const onSave = async () => {
    try {
      setErrorMessage(null);

      if (!currentRow?.id) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      await executeAction({
        variables: {
          data: {
            action: 'resendSignaturePacket',
            allocationId: currentRow.id,
            actionDataPayload: {
              subscriptionDocumentId: currentRow?.subscriptionDocument?.id
            }
          }
        }
      });

      setShowSuccessDialog(true);
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    }
  };

  const onCloseSuccessDialog = async () => {
    onClose();
    setShowSuccessDialog(false);
  };

  if (showSuccessDialog) {
    return (
      <ModalWrappedContent
        isOpen={showSuccessDialog}
        onClose={onCloseSuccessDialog}
        showCancelButton={false}
        isUseOutsideClick={false}
        confirmButton={{ name: 'OK', onClick: onCloseSuccessDialog }}
      >
        <ModalBody>
          <ApprovedIcon width={60} height={60} />
          <ModalTitle>eSignature Request Sent</ModalTitle>
          Recipient(s) will receive an email from Docusign momentarily.
        </ModalBody>
      </ModalWrappedContent>
    );
  }

  return (
    <ModalWrappedContent
      title="Resend eSignature Request"
      isOpen={isOpen}
      onClose={onClose}
      showCloseIcon={!resendESignatureRequestLoading}
      showCancelButton={!resendESignatureRequestLoading}
      isUseOutsideClick={!resendESignatureRequestLoading}
      confirmButton={{ name: 'Confirm', onClick: onSave, disabled: remainingRecipientsListLoading || resendESignatureRequestLoading }}
    >
      {remainingRecipientsListLoading || resendESignatureRequestLoading ? (
        <ModalLoader customText={remainingRecipientsListLoading ? '' : 'Please allow a few moments for this process to complete.'} />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <ModalBody>
            <p>
              This action will send another Docusign request to the next recipient(s) in the Docusign routing order: <b>{recipientsList}</b>.
              eSignature Requests were last sent to the recipient(s) on <b>{lastSentAt}</b>. Do you wish to send another eSignature Request?
            </p>
          </ModalBody>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default ResendSignaturePacket;

const ModalTitle = styled.h2`
  font-size: 33px;
  font-weight: 400;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  line-height: 22.4px;
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
